import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useTenantContext } from './useTenantContext';
import { useAuth } from './useAuth';
import { useToast } from './use-toast';
import { useVisibility } from './useVisibility';

export type ExpenseCategory = 
  | 'office_staff' 
  | 'accounting' 
  | 'legal'
  | 'telephone_systems' 
  | 'crm' 
  | 'software' 
  | 'utilities' 
  | 'rent' 
  | 'insurance' 
  | 'marketing' 
  | 'field_techs'
  | 'general'
  | 'other';

export type ExpenseFrequency = 
  | 'weekly' 
  | 'fortnightly' 
  | 'monthly' 
  | 'quarterly' 
  | 'yearly' 
  | 'one_time';

export interface CompanyExpense {
  id: string;
  company_id: string;
  category: ExpenseCategory;
  name: string;
  description: string | null;
  amount: number;
  frequency: ExpenseFrequency;
  start_date: string | null;
  end_date: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by: string;
}

export interface ExpenseInput {
  category: ExpenseCategory;
  name: string;
  description?: string;
  amount: number;
  frequency: ExpenseFrequency;
  start_date?: string;
  end_date?: string;
  is_active?: boolean;
}

export const EXPENSE_CATEGORIES: { value: ExpenseCategory; label: string }[] = [
  { value: 'office_staff', label: 'Office Staff' },
  { value: 'accounting', label: 'Accounting' },
  { value: 'legal', label: 'Legal' },
  { value: 'telephone_systems', label: 'Telephony' },
  { value: 'crm', label: 'CRM' },
  { value: 'software', label: 'Software' },
  { value: 'utilities', label: 'Utilities' },
  { value: 'rent', label: 'Rent' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'field_techs', label: 'Field Techs' },
  { value: 'general', label: 'General' },
  { value: 'other', label: 'Other' },
];

export const EXPENSE_FREQUENCIES: { value: ExpenseFrequency; label: string; monthlyMultiplier: number }[] = [
  { value: 'weekly', label: 'Weekly', monthlyMultiplier: 4.33 },
  { value: 'fortnightly', label: 'Fortnightly', monthlyMultiplier: 2.17 },
  { value: 'monthly', label: 'Monthly', monthlyMultiplier: 1 },
  { value: 'quarterly', label: 'Quarterly', monthlyMultiplier: 0.33 },
  { value: 'yearly', label: 'Yearly', monthlyMultiplier: 0.083 },
  { value: 'one_time', label: 'One-Time', monthlyMultiplier: 0 },
];

export function useCompanyExpenses() {
  const { tenantId: companyId } = useTenantContext();
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { canSeeCompanyFinancials } = useVisibility();

  const { data: expenses = [], isLoading, error } = useQuery({
    queryKey: ['company-expenses', companyId],
    queryFn: async () => {
      if (!companyId) return [];
      
      const { data, error } = await supabase
        .from('company_expenses')
        .select('*')
        .eq('company_id', companyId)
        .order('category', { ascending: true })
        .order('name', { ascending: true });

      if (error) throw error;
      return data as CompanyExpense[];
    },
    enabled: !!companyId && canSeeCompanyFinancials,
  });

  const addExpense = useMutation({
    mutationFn: async (input: ExpenseInput) => {
      if (!companyId || !user?.id) throw new Error('Missing company or user');

      const { data, error } = await supabase
        .from('company_expenses')
        .insert({
          company_id: companyId,
          created_by: user.id,
          ...input,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-expenses', companyId] });
      toast({ title: 'Expense added successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Failed to add expense', description: error.message, variant: 'destructive' });
    },
  });

  const updateExpense = useMutation({
    mutationFn: async ({ id, ...input }: ExpenseInput & { id: string }) => {
      const { data, error } = await supabase
        .from('company_expenses')
        .update(input)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-expenses', companyId] });
      toast({ title: 'Expense updated successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Failed to update expense', description: error.message, variant: 'destructive' });
    },
  });

  const deleteExpense = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('company_expenses')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-expenses', companyId] });
      toast({ title: 'Expense deleted successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Failed to delete expense', description: error.message, variant: 'destructive' });
    },
  });

  // Calculate monthly equivalent (for recurring projections)
  const calculateMonthlyAmount = (expense: CompanyExpense): number => {
    const freq = EXPENSE_FREQUENCIES.find(f => f.value === expense.frequency);
    return expense.amount * (freq?.monthlyMultiplier || 0);
  };

  // Calculate period amount (for totals - includes one-time at full value)
  const calculatePeriodAmount = (expense: CompanyExpense): number => {
    if (expense.frequency === 'one_time') {
      return expense.amount; // Full amount for one-time expenses
    }
    return calculateMonthlyAmount(expense);
  };

  /**
   * Filter expenses by a date period.
   * - One-time expenses: must have start_date within the period
   * - Recurring expenses: must be active during the period
   */
  const filterExpensesByPeriod = (
    expenseList: CompanyExpense[],
    periodStart: Date,
    periodEnd: Date
  ): CompanyExpense[] => {
    return expenseList.filter(expense => {
      // One-time expenses must have start_date within period
      if (expense.frequency === 'one_time') {
        if (!expense.start_date) return false;
        const expenseDate = new Date(expense.start_date);
        return expenseDate >= periodStart && expenseDate <= periodEnd;
      }
      
      // Recurring expenses: check if active during period
      // Active if: started before period ends AND (no end date OR ends after period starts)
      const startDate = expense.start_date 
        ? new Date(expense.start_date) 
        : new Date(0); // No start = always started
      const endDate = expense.end_date 
        ? new Date(expense.end_date) 
        : new Date(9999, 11, 31); // No end = never ends
      
      return startDate <= periodEnd && endDate >= periodStart;
    });
  };

  const activeExpenses = expenses.filter(e => e.is_active);
  
  const totalMonthlyExpenses = activeExpenses.reduce(
    (sum, expense) => sum + calculateMonthlyAmount(expense),
    0
  );

  const expensesByCategory = EXPENSE_CATEGORIES.map(cat => ({
    category: cat.value,
    label: cat.label,
    expenses: activeExpenses.filter(e => e.category === cat.value),
    monthlyTotal: activeExpenses
      .filter(e => e.category === cat.value)
      .reduce((sum, e) => sum + calculateMonthlyAmount(e), 0),
  })).filter(cat => cat.expenses.length > 0);

  return {
    expenses,
    activeExpenses,
    isLoading,
    error,
    addExpense,
    updateExpense,
    deleteExpense,
    totalMonthlyExpenses,
    expensesByCategory,
    calculateMonthlyAmount,
    calculatePeriodAmount,
    filterExpensesByPeriod,
  };
}
